import * as React from 'react';
import { GestureResponderEvent } from 'react-native/types';

import { Box, Button, Link, Text, ValidatedTextInput } from '@opendoor/bricks-next';

import { useUpdateAnswersMutation } from 'src/store/api';
import { useAppSelector } from 'src/store/hooks';
import { SellerInputAddress } from 'src/store/sellerInput';

import AddressLine from '../../components/common/AddressLine';
import ErrorNotice from '../../components/common/ErrorNotice';
import {
  isInExclusiveMarket as getIsInExclusiveMarket,
  lastCompletedSlug as getLastCompletedSlug,
} from '../../store/sellerFlow/selectors';
import { DenialHeader, DenialTextAfterHeader } from './FlowDeniedNovo';

type GeneralDenialProps = {
  address: SellerInputAddress;
  denialCode: string;
  leadToken: string;
  sellerEmail: string;
};

export const GeneralDenial: React.FC<GeneralDenialProps> = ({
  address,
  denialCode,
  leadToken,
  sellerEmail,
}) => {
  const lastCompletedSlug = useAppSelector(getLastCompletedSlug);
  const isInExclusiveMarket = useAppSelector(getIsInExclusiveMarket);

  const [email, setEmail] = React.useState(sellerEmail || '');
  const [hasError, setHasValidEmail] = React.useState(false);
  const [updateAnswers, { data, isLoading, isError }] = useUpdateAnswersMutation();

  const handleEmailInputChange = (email: string) => {
    setEmail(email);
  };

  const handleSubmitEmailForm = (e: React.FormEvent<HTMLFormElement> | GestureResponderEvent) => {
    e.preventDefault();
    updateAnswers({
      uuid: leadToken,
      completedSlug: lastCompletedSlug,
      submittedAnswers: { 'seller.email': email },
    });
  };

  const handleValidationChange = (validationState: string) => {
    setHasValidEmail(validationState != 'valid');
  };

  if (data) {
    return (
      <>
        <Box>
          <DenialHeader>We'll be in touch when we can work together</DenialHeader>
          <DenialTextAfterHeader>
            As we grow, we’ll be able to buy homes in new areas and make offers on homes that
            weren’t previously eligible.
          </DenialTextAfterHeader>
        </Box>
        <ErrorNotice />
      </>
    );
  }

  return (
    <>
      <Box>
        <DenialHeader>We can't make an offer today for your home</DenialHeader>
        <DenialTextAfterHeader>
          We may not be in your area, or we’re not confident we can share an acceptable offer based
          on your home details.
        </DenialTextAfterHeader>
      </Box>
      <Box mt={7} mb={4}>
        <Box>
          <form onSubmit={handleSubmitEmailForm} name="form" method="post">
            <ValidatedTextInput
              allowSuggestions
              analyticsName="reception-fe-flow-denied-notify-me-email"
              size="small"
              title="Email address"
              name="email"
              textContentType="emailAddress"
              prefilledValue={email}
              onChangeText={handleEmailInputChange}
              placeholder="name@example.com"
              supportText="Get notified when this changes."
              aria-label="Email"
              onValidationChange={handleValidationChange}
              format="emailAddress"
            />
            <Box pt={18}>
              {isError && (
                <Text typography="$bodyMedium" tag="p" pb={4} color="red">
                  We reached an error saving your email. Please try again.
                </Text>
              )}
              <Button
                id="od-form-submit"
                label="Continue"
                type="submit"
                disabled={hasError || !email}
                loading={isLoading}
                analyticsName="reception-fe-denied-notify-me"
                aria-label={undefined}
                size="small"
                width="100%"
                variant="primary"
                // no onclick needed since this a submit button
                onPress={handleSubmitEmailForm}
              />
            </Box>
          </form>
        </Box>
      </Box>
      <AddressLine address={address} />
      {isInExclusiveMarket && denialCode != 'address' && (
        <Box>
          <Text typography="$labelMedium" tag="label" color="$contentPrimary" mb={7}>
            Looking to buy?
          </Text>
          <Text typography="$bodyMedium" tag="p" color="$contentTertiary">
            While we can't buy your current house, we can give you an edge in finding your next
            home.{' '}
            <Link
              analyticsName="reception-fe-exclusives-early-access"
              href="https://www.opendoor.com/exclusives?ds"
            >
              Get early access
            </Link>{' '}
            to Opendoor's off-market homes you can't find anywhere else.
          </Text>
        </Box>
      )}
      <ErrorNotice />
    </>
  );
};
